import {useSelector} from 'react-redux';

import {httpMonitorAPI} from 'features/http/api/httpMonitorAPI';
import {
    getHttpMonitorsFilterArray,
    HTTP_MONITORS_DEFAULT_SORT,
    HTTP_MONITORS_ORDER_FIELDS,
} from 'features/http/lib/httpFilters';
import {useMonitorList, IMonitorListState} from 'entities/monitor';
import IOrderBy from 'shared/model/IOrderBy';
import IPagination from 'shared/api/model/IPagination';
import {userSelector, useHasTariffLimit} from 'entities/user';
import useListParams from 'shared/lib/hooks/useListParams';
import {getPollingInterval} from 'shared/api/pollingHelper';

import IHttpMonitorOutput from './HttpMonitor/IHttpMonitor';
import ISummaryHttpMonitors from './HttpMonitor/ISummaryHttpMonitors';
import {
    clearAll,
    httpMonitorSelectedIdsSelector,
    isSelectedAllSelector,
    selectAllOnPage,
} from './httpMonitorSlice';
import useCreateHttpMonitorButton from './useCreateHttpMonitorButton';

const SUMMARY_POLLING_INTERVAL_MS = 10000;

export interface IUseHttpMonitors {
    sort: IOrderBy | undefined,
    pagination: IPagination<IHttpMonitorOutput> | undefined,
    summary: ISummaryHttpMonitors | undefined,
    isLoading: boolean,
    isFetching: boolean,
    refetch: () => void,
    maxMonitors: number | undefined,
    list: IMonitorListState,
    handleCreate: () => void,
    isCreateOpen: boolean,
    isExceededOpen: boolean,
    closeCreate: () => void,
}

export const useHttpMonitors = (): IUseHttpMonitors => {
    const {args, sort, filters, hasFilters} = useListParams({
        getFilters: getHttpMonitorsFilterArray,
        defaultSort: HTTP_MONITORS_DEFAULT_SORT,
        sortFields: HTTP_MONITORS_ORDER_FIELDS,
    });
    const create = useCreateHttpMonitorButton();

    const user = useSelector(userSelector);

    const hasAccess = useHasTariffLimit('monitor_max_count_limit');

    const {data: pagination, isLoading, refetch, isFetching} =
        httpMonitorAPI.useGetHttpMonitorsQuery(args, {
            skip: !hasAccess,
            pollingInterval: getPollingInterval(),
        });

    const [getAllHttpMonitorIds] = httpMonitorAPI.useLazyGetAllHttpMonitorIdsQuery();

    const {data: summary} = httpMonitorAPI.useGetSummaryHttpMonitorsQuery(undefined, {
        skip: !hasAccess,
        pollingInterval: SUMMARY_POLLING_INTERVAL_MS,
    });

    const list = useMonitorList({
        pagination,
        isLoading,
        tariffField: 'monitor_max_count_limit',
        hasFilters,
        selection: {
            selectedIdsSelector: httpMonitorSelectedIdsSelector,
            isSelectedAllSelector,
            selectAllOnPage,
            clearAll,
        },
        getAllIds: async() => (await getAllHttpMonitorIds({sort, filters})).data,
    });

    return {
        sort,
        pagination,
        summary,
        isLoading,
        isFetching,
        refetch,
        maxMonitors: user?.tariff?.monitor_max_count_limit,
        list,
        handleCreate: create.handleOpen,
        isCreateOpen: create.isCreateOpen,
        isExceededOpen: create.isExceededOpen,
        closeCreate: create.handleClose,
    };
};

export default useHttpMonitors;
